/**
 * 保单台账页（**增 / 改 / 删 / 列**）—— B 域 B5 · 依据 B 域 PRD §5.5.5
 *
 * ## 🔴 三个字段必须来自服务端（端侧**不自行判定**）
 *
 * | 字段 | 若端侧自己算会怎样 |
 * |---|---|
 * | `cashValueCounted` | 端侧要自己判定"哪些状态计入净资产"⇒ 与内核 `cashValueCountsInNetWorth()` 分叉。B 域 PRD §9 风险 6 记的就是这个（重复计入或漏计） |
 * | `countedCashValueTotal` | 同上，且**加法本身**也要"只加计入的那些" |
 * | `insureds[].name` / `kind` | 被保人是「id 优先、姓名兜底」⇒ 端侧要 join 用户表才知道名字。服务端已 join 好下发 |
 *
 * ## 🔴 两类失败分开呈现（本批新增的交互要点）
 *
 * | 失败 | 来源 | 呈现 |
 * |---|---|---|
 * | **字段问题**（必填 / 保险期间倒序 / 现金价值 > 保额） | 本地 `buildPolicyRequest`（语义部分来自内核）**或**服务端 `400` 的 `details.issues` | 表单内**逐条**列出 |
 * | 🔴 **保单号重复**（服务端 `409`） | 唯一键校验 | 额外补一句"改保单号或先删除那一张" —— 这是**唯一"改一个字段就能过"**的失败，不指出来人只能干猜 |
 *
 * ## 关于删除
 * 用**两步内联确认**（按钮变成「确认删除」）而不是 `window.confirm()`：
 * ① 不用浏览器弹窗（与整体 UI 一致，且静态渲染下可断言）；② 破坏性动作要求**第二次点击**。
 */

import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatMoneyText, type Cents, type CreatePolicyRequest, type PolicyView } from '@jiaying/shared';
import { Loading } from '../ui/Loading.js';
import { Amount } from '../ui/Amount.js';
import { AnchorBar } from '../ui/AnchorBar.js';
import { Grid } from '../ui/Grid.js';
import { PolicyForm } from '../ui/PolicyForm.js';
import { createPolicy, deletePolicy, listPolicies, updatePolicy } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { useBaseCurrency } from '../app/useBaseCurrency.js';
import { localToday } from '../app/local-day.js';
import { policyStatusColor, policyStatusLabel } from '../features/policy-status.js';
import {
  buildPolicyRequest,
  draftFromView,
  emptyPolicyDraft,
  type PolicyDraft,
} from '../features/policy-draft.js';

/**
 * 把异常转成**给人看的逐条消息**。
 *
 * 🔴 优先用 `details.issues`（服务端 `AppException(VALIDATION_FAILED, …, { issues })` 带的结构化清单），
 * 而不是去 `message` 里按 `；` 拆 —— 拆字符串等于把**服务端的排版细节**当成接口。
 * 没有 `issues` 时回落到 `message`（中文原文，**原样展示、不重写**）。
 */
function apiMessages(e: unknown): readonly string[] {
  if (!(e instanceof ApiError)) return [String(e)];
  const details = e.body.details as { issues?: unknown } | undefined;
  const issues = Array.isArray(details?.issues)
    ? details.issues.filter((x): x is string => typeof x === 'string')
    : [];
  if (issues.length > 0) return issues;
  return [e.message];
}

export function PoliciesPage() {
  const qc = useQueryClient();
  const policies = useQuery({ queryKey: ['policies'], queryFn: listPolicies });
  // 🔴 `DR-036` D1：**保单表没有币种类** ⇒ 隐含本币；拿不到本币就**不该渲染金额**
  //    （不用 `'CNY'` 兜底 —— 那会把"不知道"伪装成"人民币"）
  const baseCurrency = useBaseCurrency();

  /** `null` = 表单关闭；`'create'` = 新建；其它 = 正在编辑该 id */
  const [editing, setEditing] = useState<'create' | string | null>(null);
  const [draft, setDraft] = useState<PolicyDraft>(() => emptyPolicyDraft(localToday()));
  const [errors, setErrors] = useState<readonly string[]>([]);
  /** 两步删除：非空 = 正在等第二次点击确认 */
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  /**
   * 🔴 内容区引用 —— 交给 `<AnchorBar>` 做吸顶页内导航（`AC-O2.36`）。
   * 必须指向**内容区的直接容器**（本页 = 最外层 `<Stack>`），理由见 `AnchorBar.tsx` 使用纪律 §1。
   */
  const contentRef = useRef<HTMLDivElement | null>(null);

  const close = (): void => {
    setEditing(null);
    setErrors([]);
    setConfirmingId(null);
  };

  const save = useMutation({
    mutationFn: (req: CreatePolicyRequest): Promise<PolicyView> =>
      editing === null || editing === 'create' ? createPolicy(req) : updatePolicy(editing, req),
    onSuccess: () => {
      close();
      // 🔴 现金价值计入净资产 ⇒ 净资产缓存也必须失效（否则首页 Hero 是旧数）
      void qc.invalidateQueries({ queryKey: ['policies'] });
      void qc.invalidateQueries({ queryKey: ['networth'] });
    },
    onError: (e: unknown) => {
      const msgs = apiMessages(e);
      setErrors(
        e instanceof ApiError && e.status === 409
          ? [...msgs, '请改用别的保单号，或先删除那一张']
          : msgs,
      );
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deletePolicy(id),
    onSuccess: () => {
      close();
      void qc.invalidateQueries({ queryKey: ['policies'] });
      void qc.invalidateQueries({ queryKey: ['networth'] });
    },
    onError: (e: unknown) => setErrors(apiMessages(e)),
  });

  const openCreate = (): void => {
    setDraft(emptyPolicyDraft(localToday()));
    setErrors([]);
    setConfirmingId(null);
    setEditing('create');
  };

  const openEdit = (p: PolicyView): void => {
    setDraft(draftFromView(p));
    setErrors([]);
    setConfirmingId(null);
    setEditing(p.id);
  };

  const submit = (): void => {
    const built = buildPolicyRequest(draft);
    if (!built.ok) {
      setErrors(built.errors);
      return;
    }
    setErrors([]);
    save.mutate(built.request);
  };

  // ── 守卫（**都在 hooks 之后** —— 顺序不可调换，否则 hook 数量会随分支变化）──
  if (policies.isPending) return <Loading />;
  if (policies.isError) return <Alert severity="error">保单读取失败：{String(policies.error)}</Alert>;
  if (baseCurrency === null) return <Loading />;

  const { items, groupCounts, countedCashValueTotal } = policies.data;
  const busy = save.isPending || remove.isPending;

  return (
    <Stack spacing={2} ref={contentRef}>
      {/* 🆕 🔴 吸顶页内导航（`AC-O2.36`）—— 必须是内容区第一个子节点；短页自动不产出 */}
      <AnchorBar contentRef={contentRef} />

      {/*
        🆕 网格化（㊱）：概览卡与表单**等宽双列**（设计 `.g2`）。
        🔴 为什么不把列表也放进去：列表行右侧有「编辑 / 删除」两个按钮 + 左侧多行 secondary，
        挤到半宽（≈640px）会让每行折成三行，可读性反而变差 ⇒ **列表整行**。
        （这与设计 `14-资产_原型.html:532` 的处理一致：网格用于**卡片组**，表格/列表另走整宽。）
      */}
      <Grid cols={2} sx={{ alignItems: 'start' }}>
        <Card data-anchor="计入净资产的现金价值">
          <CardContent>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  计入净资产的现金价值（服务端算好下发 · 仅生效中 / 已缴清）
                </Typography>
                <Amount
                  cents={countedCashValueTotal as Cents}
                  currency={baseCurrency}
                  colored={false}
                  variant="h5"
                  sx={{ fontWeight: 700, my: 0.5 }}
                />
              </Box>
              <Button variant="contained" onClick={openCreate} disabled={editing !== null} sx={{ flexShrink: 0 }}>
                新增保单
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
              {groupCounts.map((g) => (
                <Chip key={g.company} size="small" variant="outlined" label={`${g.company} · ${g.count}`} />
              ))}
              {groupCounts.length === 0 && <Chip size="small" variant="outlined" label="暂无保单" />}
            </Stack>
          </CardContent>
        </Card>

        {/* 🔴 表单**内联**（不是 Dialog）—— 理由见 `PolicyForm` 文件头；空态时这一列给出说明 */}
        {editing !== null ? (
          <PolicyForm
            draft={draft}
            onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
            errors={errors}
            busy={busy}
            mode={editing === 'create' ? 'create' : 'edit'}
            currency={baseCurrency}
            onSubmit={submit}
            onCancel={close}
          />
        ) : (
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                保单台账
              </Typography>
              <Typography variant="body2" color="text.secondary">
                录入 / 修改 / 删除都在这一本台账上 —— 服务端含 <strong>1:N 被保人</strong>，
                保单号账本内唯一（重复会返回 409）。
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                现金价值<b>按状态</b>决定是否计入净资产（只有生效中 / 已缴清计入）——
                这个判定由服务端算好下发，端侧不自行判断。
              </Typography>
            </CardContent>
          </Card>
        )}
      </Grid>

      <Card data-anchor="保单台账">
        <CardContent sx={{ pb: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            保单（{items.length}）
          </Typography>
        </CardContent>
        <Divider />
        <List dense>
          {items.map((p) => (
            /**
             * 🔴 **不用 `secondaryAction`**（`2026-10-13` 与账户页同因修复）。
             *
             * MUI 的 `secondaryAction` 是绝对定位 + 给 `ListItem` 固定 `padding-right: 48`
             * 来"避免碰撞"，但本行是**两个中文按钮**（编辑 + 删除），实测宽约 **132px**
             * ⇒ 48px 保护带不够，金额被压进按钮区间。
             *
             * 无头实测证据见 `_archive/e2e-b70-accounts-overlap.mjs`（账户页同型：
             * `按钮左 − 金额右 = −100`）。修法 = 让按钮回到文档流（flex 行），
             * 宽度由内容撑开 ⇒ 重叠在**布局层面不可能**，而非靠某个写死的像素够大。
             */
            <ListItem key={p.id} divider alignItems="flex-start" disablePadding sx={{ px: 2, py: 1 }}>
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <span>{p.policyNo}</span>
                    <Chip
                      size="small"
                      color={policyStatusColor(p.status)}
                      label={policyStatusLabel(p.status)}
                    />
                  </Stack>
                }
                secondary={
                  <>
                    {p.company} · {p.insuranceType}
                    <br />
                    投保人 {p.policyholder.name}
                    {p.insureds.length > 0 && ` · 被保人 ${p.insureds.map((i) => i.name).join('、')}`}
                    {p.beneficiary.name !== '' && ` · 受益人 ${p.beneficiary.name}`}
                    <br />
                    保额 {formatMoneyText(p.sumAssured as Cents, baseCurrency)} · {p.startDate} 至{' '}
                    {p.endDate ?? '（长期）'}
                    {p.cashValueCounted ? ' · ✅ 现金价值计入净资产' : ' · ⚠️ 现金价值不计入'}
                  </>
                }
              />
              {/* 🔴 金额与按钮同在一个 flex 行（与账户页同一修法） */}
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ ml: 1, flexShrink: 0, flexWrap: 'nowrap' }}
              >
                <Amount
                  cents={p.cashValue as never}
                  currency={baseCurrency}
                  colored={false}
                  sx={{ flexShrink: 0 }}
                />
                {confirmingId === p.id ? (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      color="error"
                      variant="contained"
                      disabled={busy}
                      onClick={() => remove.mutate(p.id)}
                      data-testid={`policy-delete-confirm-${p.id}`}
                    >
                      确认删除
                    </Button>
                    <Button size="small" onClick={() => setConfirmingId(null)} disabled={busy}>
                      取消
                    </Button>
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      onClick={() => openEdit(p)}
                      disabled={editing !== null || busy}
                      data-testid={`policy-edit-${p.id}`}
                    >
                      编辑
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setConfirmingId(p.id)}
                      disabled={editing !== null || busy}
                      data-testid={`policy-delete-${p.id}`}
                    >
                      删除
                    </Button>
                  </Stack>
                )}
              </Stack>
            </ListItem>
          ))}
          {items.length === 0 && editing === null && (
            <ListItem>
              <ListItemText secondary="还没有保单 —— 点右上角「新增保单」录入第一张（服务端台账含 1:N 被保人）" />
            </ListItem>
          )}
        </List>
      </Card>

      {/* 删除 / 保存失败（`409` / 网络 / 权限）在**表单关闭时**也要看得见 —— 故单独一处 */}
      {errors.length > 0 && editing === null && (
        <Alert severity="error" data-testid="policy-page-errors">
          <Stack component="ul" sx={{ m: 0, pl: 2 }}>
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}
